import type { Metrics, Stroke } from './types'

/**
 * Моторика рисования — второй канал состояния (первый — самоотчёт SAM).
 * Считается на клиенте, чтобы работать оффлайн; сырые штрихи всё равно
 * уезжают на сервер — это главный датасет продукта.
 */
export function computeMetrics(strokes: Stroke[]): Metrics {
  let dist = 0
  let time = 0
  let turns = 0
  let sharp = 0
  const speeds: number[] = []
  let t0 = Infinity
  let t1 = 0

  for (const st of strokes) {
    const p = st.pts
    if (p.length < 2) continue
    t0 = Math.min(t0, p[0].t)
    t1 = Math.max(t1, p[p.length - 1].t)
    for (let i = 1; i < p.length; i++) {
      const dx = p[i].x - p[i - 1].x
      const dy = p[i].y - p[i - 1].y
      const dt = Math.max(p[i].t - p[i - 1].t, 1)
      const d = Math.hypot(dx, dy)
      dist += d
      time += dt
      speeds.push(d / dt)
      if (i > 1) {
        const ex = p[i - 1].x - p[i - 2].x
        const ey = p[i - 1].y - p[i - 2].y
        const a1 = Math.atan2(ey, ex)
        const a2 = Math.atan2(dy, dx)
        let da = Math.abs(a2 - a1)
        if (da > Math.PI) da = 2 * Math.PI - da
        if (d > 2 && Math.hypot(ex, ey) > 2) {
          turns++
          if (da > Math.PI / 3) sharp++
        }
      }
    }
  }

  const wall = t1 - t0 || 1
  const mean = speeds.length ? speeds.reduce((a, b) => a + b, 0) / speeds.length : 0
  const varr = speeds.length ? speeds.reduce((a, b) => a + (b - mean) ** 2, 0) / speeds.length : 0

  return {
    strokes: strokes.length,
    avgSpeed: +mean.toFixed(2),
    speedCV: mean ? +(Math.sqrt(varr) / mean).toFixed(2) : 0,
    sharpPct: turns ? Math.round((100 * sharp) / turns) : 0,
    pausePct: Math.round((100 * Math.max(0, wall - time)) / wall),
    totalSec: +(wall / 1000).toFixed(1),
  }
}

export type StrokeQuality = { sharp: number; speed: number; dur: number; len: number }

export function strokeQuality(st: Stroke): StrokeQuality {
  const m = computeMetrics([st])
  const p = st.pts
  let len = 0
  for (let i = 1; i < p.length; i++) len += Math.hypot(p[i].x - p[i - 1].x, p[i].y - p[i - 1].y)
  const last = p[p.length - 1]
  return { sharp: m.sharpPct, speed: m.avgSpeed, dur: last ? last.t - p[0].t : 0, len }
}
